import React, { useState, useEffect, useCallback } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import { StatusActionButton, StatsGrid, StatItem } from '../../../shared/ui';
import { contractsV2Api, ContractV2 } from '../../../api/contractsV2Api';
import { sitesV2Api } from '../../../api/sitesV2Api';
import { siteMemberPermissionsApi } from '../../../api/siteMemberPermissionsApi';
import { useToast } from '../../../shared/contexts/ToastContext';
import { useAuth } from '../../../shared/contexts/AuthContext';
import CreateContractModal from './CreateContractModal';
import EditContractModal from './EditContractModal';
import './ContractsCard.css';

interface ContractsCardProps {
  siteId: number;
}

const ContractsCard: React.FC<ContractsCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [contracts, setContracts] = useState<ContractV2[]>([]);
  const [statistics, setStatistics] = useState<{
    total: number;
    active: number;
    closed: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingContractId, setEditingContractId] = useState<number | null>(null);
  const [hasFinanceViewPermission, setHasFinanceViewPermission] = useState(false);
  const [hasFinanceEditPermission, setHasFinanceEditPermission] = useState(false);
  const { showError, showSuccess } = useToast();
  const { user } = useAuth();

  const checkFinancePermission = useCallback(async () => {
    try {
      const members = await sitesV2Api.getSiteMembers(siteId);
      const currentUserMember = members.find(m => m.userId === user?.id);

      if (currentUserMember) {
        const perms = await siteMemberPermissionsApi.getMemberPermissions(siteId, currentUserMember.id);
        setHasFinanceViewPermission(perms.some(p => p.permissionCode === 'FINANCE_VIEW_DATA'));
        setHasFinanceEditPermission(perms.some(p => p.permissionCode === 'FINANCE_EDIT'));
      } else {
        setHasFinanceViewPermission(false);
        setHasFinanceEditPermission(false);
      }
    } catch {
      setHasFinanceViewPermission(false);
      setHasFinanceEditPermission(false);
    }
  }, [siteId, user?.id]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      await checkFinancePermission();

      try {
        const data = await contractsV2Api.getSiteContracts(siteId, { onlyActive: false });
        setContracts(data);
        setStatistics({
          total: data.length,
          active: data.filter(c => c.isActive && !c.isClosed).length,
          closed: data.filter(c => c.isClosed).length
        });
      } catch (err: unknown) {
        const status = err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : null;
        if (status === 403) {
          setHasFinanceViewPermission(false);
          setHasFinanceEditPermission(false);
          setContracts([]);
          setStatistics(null);
        } else {
          console.error('Error loading contracts:', err);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, checkFinancePermission]);

  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  const handleContractCreated = () => {
    setShowCreateModal(false);
    loadData();
    showSuccess('Контракт создан');
  };

  const handleContractUpdated = () => {
    setEditingContractId(null);
    loadData();
    showSuccess('Контракт обновлён');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('ru-RU').format(amount) + ' ₽';
  };

  const statsData: StatItem[] = [
    { id: 'total', value: statistics?.total ?? 0, label: 'Всего', icon: 'ri-file-contract-line', color: 'muted' },
    { id: 'active', value: statistics?.active ?? 0, label: 'Активных', icon: 'ri-checkbox-circle-line', color: 'success' },
    { id: 'closed', value: statistics?.closed ?? 0, label: 'Закрытых', icon: 'ri-archive-line', color: 'error' }
  ];

  return (
    <DashboardCard
      id="contracts-card"
      title="Контракты"
      icon="ri-file-contract-line"
      isCollapsible
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      onFullscreenOpenChange={setIsFullscreenOpen}
      expandMode="fullscreen"
      className="contracts-card"
    >
      {!hasFinanceViewPermission ? (
        <div className="contracts-no-permission">
          <i className="ri-lock-line"></i>
          <p>Нет прав для просмотра контрактов</p>
        </div>
      ) : (
        <>
          {hasFinanceEditPermission && (
            <div className="contracts-actions">
              <StatusActionButton
                variant="primary"
                fullWidth
                onClick={() => setShowCreateModal(true)}
                disabled={loading}
              >
                Создать контракт
              </StatusActionButton>
            </div>
          )}

          {statistics && <StatsGrid items={statsData} />}

          {contracts.length === 0 ? (
            <div className="contracts-empty">
              <i className="ri-file-contract-line"></i>
              <p>Контракты не созданы</p>
              {hasFinanceEditPermission && (
                <p className="contracts-empty-hint">Нажмите «Создать контракт» для добавления</p>
              )}
            </div>
          ) : (
            <div className="contracts-list">
              {contracts.map((contract) => (
                <div
                  key={contract.id}
                  className={`contract-item ${contract.isClosed ? 'closed' : ''}`}
                >
                  <div className="contract-header">
                    <h3 className="contract-title">{contract.title}</h3>
                    <span className={`contract-status ${contract.isActive && !contract.isClosed ? 'active' : 'closed'}`}>
                      {contract.isActive && !contract.isClosed ? 'Активен' : 'Закрыт'}
                    </span>
                  </div>
                  {contract.description && (
                    <p className="contract-description">{contract.description}</p>
                  )}
                  <div className="contract-meta">
                    <span className="contract-type">
                      {contract.typeOfWork?.name ?? 'Вид работы'}
                    </span>
                    <span className="contract-amount">{formatAmount(contract.fixedAmount)}</span>
                  </div>
                  <div className="contract-footer">
                    <span className="contract-date">
                      Создан: {formatDate(contract.dateCreated)}
                    </span>
                    {hasFinanceEditPermission && !contract.isClosed && (
                      <button
                        type="button"
                        className="contract-edit-btn"
                        onClick={() => setEditingContractId(contract.id)}
                        title="Редактировать"
                      >
                        <i className="ri-pencil-line"></i> Редактировать
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <CreateContractModal
            isOpen={showCreateModal}
            siteId={siteId}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleContractCreated}
          />
          <EditContractModal
            isOpen={editingContractId !== null}
            siteId={siteId}
            contractId={editingContractId}
            onClose={() => setEditingContractId(null)}
            onSuccess={handleContractUpdated}
          />
        </>
      )}
    </DashboardCard>
  );
};

export default ContractsCard;
